import React, { useMemo } from 'react';
import { Grid, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useGetSearchLabels } from 'modules/Insights/hooks/useGetSearchLabels';
import { OrgAnalyticsContext, OrgAnalyticsDimensionType } from 'graphql/types';
import { useGetSupervisorAnalyticsData } from 'modules/Insights/Notes/hooks/useGetSupervisorAnalyticsData';
import TeamProductivityDurationControl from './TeamProductivityDurationControl';
import TeamProductivityLegend from './TeamProductivityLegend';
import { resultDataSetProp, transformAnalyticsData } from './utils';
import TeamProductivitySkeleton from './TeamProductivitySkeleton';
import TeamProductivityNoData from './TeamProductivityNoData';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '60%',
    minHeight: theme.spacing(12.75),
    margin: theme.spacing(0, 2, 0, 0),
    padding: theme.spacing(1.75, 1.25, 1.75, 1.25)
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  chart: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0.25),
    width: '60%'
  },
  legend: {
    display: 'flex',
    margin: theme.spacing(0.25),
    width: '40%'
  }
}));

const TeamProductivityChart: React.FC = () => {
  const classes = useStyles();

  const labelValues = useGetSearchLabels();

  const { data, isEmpty, loading } = useGetSupervisorAnalyticsData(
    OrgAnalyticsDimensionType.MetricsByHoursSummary,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const transformData = useMemo(() => {
    if (isEmpty) {
      return { dataSet: [] as resultDataSetProp[][], scheduledHours: 0 };
    } else return transformAnalyticsData(data);
  }, [isEmpty, data]);

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'team-productivity-chart-container'}
    >
      {loading ? (
        <TeamProductivitySkeleton />
      ) : isEmpty ? (
        <TeamProductivityNoData />
      ) : (
        <Grid
          data-testid={'team-productivity-chart'}
          className={classes.content}
        >
          <Grid className={classes.chart}>
            <TeamProductivityDurationControl
              data={transformData?.dataSet}
              scheduledHours={transformData?.scheduledHours}
            />
          </Grid>
          <Grid className={classes.legend}>
            <TeamProductivityLegend data={transformData?.dataSet} />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};
export default React.memo(TeamProductivityChart);
